// Tab "Einstellungen" (E1-E19 of APP-PARITY.md; since 15.10.2026 the machine only, as the local app), after the UX line
// of 19.10.2026: the main page answers "when does it go on and off?" (standby, switching on with the schedule's
// summary), then rows to the pages Waage, Wassertank, Display, Claude, Fortgeschritten, System, and the machine
// (unpairing). The weekly plan with the holiday and each of those is a page of its own, built as its board; System
// holds everything about the device, from the firmware to the resets (Dominik 23.10.2026, until then in Wartung).
// Decisions in core/screens/settings.ts.
import { useId, useState, type ReactNode } from 'react';
import {
  connLine,
  deviceView,
  holidayCommand,
  pairingErrorText,
  planEdit,
  planOffFor,
  powerView,
  scaleView,
  scheduleHead,
  scheduleSummaryParts,
  settingsCards,
  shotLine,
  dotJoin,
  t,
  type ControlView,
  type PairingApi,
  type PlanEdit,
  type PlanSlot,
  type SettingsPage,
} from '../../core/index.js';
import {
  Collapse,
  Button,
  Card,
  CardHeader,
  Chip,
  DateField,
  FitLine,
  Icon,
  NavList,
  Sheet,
  StatusDot,
  TimeWheel,
  TitleInfo,
  Toggle,
} from '../components/index.js';
import { useMachine, useStore } from '../machine-context.js';
import { ActionButton, ControlRow, ControlSwitch, OwnerNote, Row, SubScreen, TabScreen } from './parts.js';
import { DeviceCard } from '../device/DeviceCard.js';
import { DiagnosisBox } from '../device/DiagnosisBox.js';
import { ExportCard } from '../device/ExportCard.js';
import { ResetCard } from '../device/ResetCard.js';
// Claude support (src/ui/ai, feature/claude-assist)
import { ClaudeCard } from '../ai/ClaudeCard.js';
import { useAssistant } from '../ai/use-assistant.js';

export function SettingsTab({
  title,
  accountButton,
  api,
  onUnpaired,
  page = { page: 'main' },
  onPage = () => undefined,
  onBack = () => undefined,
}: {
  title: string;
  accountButton?: ReactNode;
  api: PairingApi;
  onUnpaired: () => void;
  /** the sub-page shown (#einstellungen/zeitplan …), the main page by default */
  page?: SettingsPage;
  onPage?: (p: SettingsPage) => void;
  onBack?: () => void;
}) {
  switch (page.page) {
    case 'schedule':
      return <SchedulePage onBack={onBack} />;
    case 'scale':
      return (
        <Page titleKey="sScale" testId="scale-page" onBack={onBack}>
          <ScaleCards />
        </Page>
      );
    case 'tank':
      return (
        <Page titleKey="sTank" testId="tank-page" onBack={onBack}>
          <TankCards />
        </Page>
      );
    case 'display':
      return (
        <Page titleKey="sDisplay" testId="display-page" onBack={onBack}>
          <DisplayCards />
        </Page>
      );
    case 'claude':
      return (
        <Page titleKey="sClaude" testId="claude-page" onBack={onBack}>
          <ClaudeCard />
        </Page>
      );
    case 'advanced':
      return (
        <Page titleKey="sPid" testId="advanced-page" onBack={onBack}>
          <AdvancedCards />
        </Page>
      );
    case 'system':
      return (
        <Page titleKey="sSystem" testId="system-page" onBack={onBack}>
          <SystemCards onEnded={onUnpaired} />
        </Page>
      );
    default:
      return (
        <TabScreen tab="einstellungen" title={title} accountButton={accountButton}>
          <MainOwnerNote />
          <StandbyCard />
          <WakeCard onPage={onPage} />
          <MoreCard onPage={onPage} />
          <MachineCard api={api} onUnpaired={onUnpaired} />
        </TabScreen>
      );
  }
}

function Page({
  titleKey,
  testId,
  onBack,
  children,
}: {
  titleKey: 'sScale' | 'sTank' | 'sDisplay' | 'sClaude' | 'sPid' | 'sSystem' | 'scheduleTitle';
  testId: string;
  onBack: () => void;
  children: ReactNode;
}) {
  const { ctx } = useMachine();
  return (
    <SubScreen tab="einstellungen" title={ctx.tr(titleKey)} onBack={onBack} testId={testId}>
      {children}
    </SubScreen>
  );
}

/**
 * A value as a row (prototype "Reiter Einstellungen": "Nach · 30 min ›"): a tap opens a sheet with what changes it. The
 * sheet's title stands alone (`title`, "Standby nach" for the row "Nach"), its switch in the header (`aside`), so the
 * first row never says the title again (23.10.2026: "Uhr im Standby | Uhr im Standby"). "Fertig" closes it; a sheet
 * that sends only on its own button (the holiday) gets `children` as a function with `close` and `done={false}`.
 */
function SheetRow({
  label,
  title = label,
  value,
  hint,
  icon,
  aside,
  done = true,
  children,
  testId,
}: {
  label: string;
  title?: string;
  value?: ReactNode;
  hint?: ReactNode;
  icon?: 'clock';
  aside?: ReactNode;
  done?: boolean;
  children: ReactNode | ((close: () => void) => ReactNode);
  testId: string;
}) {
  const { ctx } = useMachine();
  const [open, setOpen] = useState(false);
  const titleId = useId();
  const close = () => setOpen(false);
  return (
    <>
      <NavList
        items={[
          {
            key: testId,
            label,
            ...(hint !== undefined ? { hint } : {}),
            ...(value !== undefined ? { value } : {}),
            ...(icon ? { icon } : {}),
            onClick: () => setOpen(true),
            testId: `${testId}-open`,
          },
        ]}
      />
      {open && (
        <Sheet
          variant="sheet"
          title={title}
          titleId={titleId}
          headerAside={aside}
          onClose={close}
          testId={`${testId}-sheet`}
        >
          {typeof children === 'function' ? children(close) : children}
          {done && (
            <Button block onClick={close} data-testid={`${testId}-done`}>
              {ctx.tr('done')}
            </Button>
          )}
        </Sheet>
      )}
    </>
  );
}

/** the lowest temperature − reaches while keeping warm is on; off is the switch (23.10.2026) */
const WARM_MIN_C = 50;

/**
 * Keeping warm in standby (Dominik 23.10.2026: „bei standby warm halten muss man solange auf minus druecken bis man bei
 * aus landet“): the switch in the sheet's header turns it on at 70 °C and off at 0; while it is on, the temperature
 * (never down to 0) and when it goes fully off. The same sheet on the main page and on the schedule's page.
 */
function WarmSheetRow({
  label,
  value,
  hint,
  testId,
}: {
  label: string;
  value?: ReactNode;
  hint?: ReactNode;
  testId: string;
}) {
  const { ctx } = useMachine();
  const p = powerView(ctx);
  const warm = p.warmTemp;
  if (!warm) return null;
  const on = typeof warm.value === 'number' && warm.value > 0;
  return (
    <SheetRow
      label={label}
      title={ctx.tr('keepWarm')}
      {...(value !== undefined ? { value } : {})}
      {...(hint !== undefined ? { hint } : {})}
      aside={<ControlSwitch control={warm} testId={`${testId}-on`} />}
      testId={testId}
    >
      <Collapse open={on}>
        <div className="stack">
          <ControlRow
            control={{ ...warm, label: ctx.tr('warmTempShort') }}
            options={{ min: WARM_MIN_C }}
            testId={`${testId}-temp`}
          />
          <ControlRow control={p.warmHours} testId={`${testId}-hours`} />
        </div>
      </Collapse>
    </SheetRow>
  );
}

/** For a member, once over the page: why standby and switching on can't be changed (no sentence at every row). */
function MainOwnerNote() {
  const { ctx } = useMachine();
  const p = powerView(ctx);
  return (
    <OwnerNote controls={[p.standby, p.standbyTime, p.warmTemp, p.displayOff, p.warmupFlush, p.schedule]} />
  );
}

/**
 * Standby (prototype "Reiter Einstellungen"): the switch, then the values as rows (after how long, keeping warm, the
 * clock on the display); a row opens its sheet.
 */
function StandbyCard() {
  const { ctx } = useMachine();
  const p = powerView(ctx);
  return (
    <Card className="tab-card rows-card" data-testid="power-card">
      <CardHeader title={ctx.tr('standby')} />
      <ControlRow control={p.standby} testId="standby-enabled" />
      {/* the rows the switch shows open and close instead of jumping (Collapse) */}
      <Collapse open={!!p.standbyTime}>
        {p.standbyTime && (
          <SheetRow
            label={ctx.tr('standbyAfter')}
            title={ctx.tr('standbyAfterTitle')}
            value={p.standbyTime.text}
            testId="standby-time"
          >
            <ControlRow control={p.standbyTime} bare testId="standby-time" />
          </SheetRow>
        )}
      </Collapse>
      <Collapse open={!!p.warmTemp}>
        {p.warmTemp && <WarmSheetRow label={p.warmTemp.label} value={p.warmTemp.text} testId="warm" />}
      </Collapse>
      <Collapse open={!!p.displayOff}>
        {p.displayOff && (
          // a choice of minutes, not a switch (board "Reiter Einstellungen" since 23.10.2026): the firmware keeps how
          // long the clock stays
          <SheetRow
            label={p.displayOff.label}
            value={p.displayOff.options.find((o) => o.on)?.label ?? p.displayOff.text}
            testId="display-off"
          >
            <ControlRow control={p.displayOff} bare testId="display-off" />
          </SheetRow>
        )}
      </Collapse>
    </Card>
  );
}

/** Switching on (prototype): the schedule as a row with its summary, the flush after a cold start. */
function WakeCard({ onPage }: { onPage: (p: SettingsPage) => void }) {
  const { ctx } = useMachine();
  const p = powerView(ctx);
  return (
    <Card className="tab-card rows-card" data-testid="wake-card">
      <CardHeader title={ctx.tr('sWake')} />
      <NavList
        items={[
          {
            key: 'schedule',
            label: ctx.tr('scheduleTitle'),
            // one line, the days that do not fit drop out from the end (WRITING.md 0a)
            hint: <FitLine parts={scheduleSummaryParts(ctx)} />,
            onClick: () => onPage({ page: 'schedule' }),
            testId: 'schedule-open',
          },
        ]}
      />
      <ControlRow control={p.warmupFlush} testId="warmup-flush" />
      <Collapse open={!!p.warmupWait}>
        {p.warmupWait && (
          <SheetRow label={p.warmupWait.label} value={p.warmupWait.text} testId="warmup-wait">
            <ControlRow control={p.warmupWait} bare testId="warmup-wait" />
          </SheetRow>
        )}
      </Collapse>
    </Card>
  );
}

/**
 * The schedule (prototype "Zeitplan"): its switch in the top bar, when it switches on (workdays big, the weekend in a
 * line), the week, then rows for the holiday, standby and keeping warm; a row opens its sheet.
 */
function SchedulePage({ onBack }: { onBack: () => void }) {
  const { ctx, send } = useMachine();
  const p = powerView(ctx);
  const head = scheduleHead(ctx);
  const on = ctx.value('schedule.enabled') === true;
  const sw = p.schedule && (
    <Toggle
      id="schedule-switch"
      label={ctx.tr('scheduleOn')}
      hideLabel
      checked={on}
      disabled={!p.schedule.editable}
      onChange={(e) => void send({ kind: 'set', params: { 'schedule.enabled': e.target.checked } })}
      data-testid="schedule-switch"
    />
  );
  return (
    <SubScreen
      tab="einstellungen"
      title={ctx.tr('scheduleTitle')}
      onBack={onBack}
      right={sw}
      testId="schedule-page"
    >
      {head?.empty ? (
        <Card className="tab-card" data-testid="schedule-head">
          <p className="hint">{head.empty}</p>
        </Card>
      ) : head ? (
        <Card className="tab-card sched-head" data-testid="schedule-head">
          <TitleInfo info={head.info} label={ctx.tr('infoShow')} testId="schedule-info">
            <span className="label-dim">{head.label}</span>
          </TitleInfo>
          <span className="sched-time num">{head.time}</span>
          {(head.line || head.preheat) && (
            <span className="label-dim" data-testid="schedule-line">
              {[head.line, head.preheat].filter(Boolean).join(' ')}
            </span>
          )}
        </Card>
      ) : (
        <Card className="tab-card" data-testid="schedule-off">
          <p className="hint">{ctx.tr('scheduleOffText')}</p>
        </Card>
      )}
      {p.plan && (
        <Card className="tab-card" data-testid="week-card">
          <CardHeader
            title={ctx.tr('planWeek')}
            info={{ text: ctx.tr('planTapHint'), label: ctx.tr('infoShow'), testId: 'plan-info' }}
          />
          <PlanBox />
        </Card>
      )}
      <Card className="tab-card rows-card" data-testid="schedule-rows">
        {p.holiday && (
          <SheetRow
            label={p.holiday.label}
            hint={p.holiday.until ?? ctx.tr('holidayRowHint')}
            done={false}
            testId="holiday"
          >
            {(close) => <HolidaySheet onDone={close} />}
          </SheetRow>
        )}
        {p.standby && (
          <SheetRow
            label={ctx.tr('standby')}
            hint={p.standbyTime ? ctx.tr('standbyRowHint', { n: ctx.num('standby.time') }) : ctx.tr('off')}
            aside={<ControlSwitch control={p.standby} testId="sched-standby-enabled" />}
            testId="sched-standby"
          >
            <Collapse open={!!p.standbyTime}>
              <ControlRow control={p.standbyTime} testId="sched-standby-time" />
            </Collapse>
          </SheetRow>
        )}
        {p.warmTemp && <WarmSheetRow label={ctx.tr('keepWarm')} hint={p.warmTemp.text} testId="sched-warm" />}
      </Card>
    </SubScreen>
  );
}

/** Times a day the firmware keeps (lib/Orione/src/OrioneCare.h kWindows): the columns of the week. */
const PLAN_COLUMNS = 2;

/**
 * Weekly plan, Monday to Sunday, up to 2 times a day (prototype "Zeitplan"): a row per day with its times as equal
 * fields in columns, so they line up; today framed. Each time is its own field (Dominik 22.10.2026: a tap on "Zeit 2"
 * opened time 1, a second time needed "Noch eine Zeit" first): a tap opens that time in a sheet, the day's next free
 * field shows "+" and adds a time there. Every edit is the whole plan in one set.
 */
function PlanBox() {
  const { ctx } = useMachine();
  const plan = powerView(ctx).plan;
  const [open, setOpen] = useState<{ day: number; slot: number } | null>(null);
  if (!plan) return null;
  // the browser's weekday (the plan is in the machine's zone; the zone hint says so when they differ)
  const today = (new Date(ctx.now).getDay() + 6) % 7;
  const slot = open && plan.days[open.day]?.slots[open.slot];
  return (
    <div className="plan" data-testid="plan">
      {plan.zoneHint && <p className="hint">{plan.zoneHint}</p>}
      <div className="week-row week-head" aria-hidden="true">
        <span />
        {Array.from({ length: PLAN_COLUMNS }, (_, k) => (
          <span key={k} className="week-col">
            {ctx.tr('planSlot', { n: k + 1 })}
          </span>
        ))}
      </div>
      <ul className="week">
        {plan.days.map((d) => (
          <li
            key={d.index}
            className={d.index === today ? 'week-row is-today' : 'week-row'}
            data-testid={`planday-${d.index}`}
          >
            <span className="week-day" aria-hidden="true">
              {d.name}
            </span>
            {d.slots.map((s, k) =>
              s.inert ? (
                <span key={k} className="week-slot" aria-hidden="true">
                  –
                </span>
              ) : (
                <button
                  key={k}
                  type="button"
                  className={s.text ? 'week-slot is-on num' : 'week-slot is-free'}
                  aria-label={s.label}
                  disabled={!plan.editable}
                  onClick={() => setOpen({ day: d.index, slot: k })}
                  data-testid={`planslot-${d.index}-${k}`}
                >
                  {s.text ?? <Icon name="plus" size="sm" />}
                </button>
              ),
            )}
          </li>
        ))}
      </ul>
      <p className="week-legend">
        <span className="week-key" aria-hidden="true" />
        {ctx.tr('planOnReady')}
      </p>
      {open && slot && <PlanSlotSheet day={open.day} slot={slot} onClose={() => setOpen(null)} />}
    </div>
  );
}

/**
 * One time of a day (prototype "Zeitplan: eine Zeit ändern"): the ready time on wheels that are there at once (no
 * time field that opens its picker on a second tap), the off time behind a switch, then save, the same for all days,
 * delete. Nothing is sent before "Speichern".
 */
function PlanSlotSheet({ day, slot, onClose }: { day: number; slot: PlanSlot; onClose: () => void }) {
  const { ctx, send } = useMachine();
  const titleId = useId();
  const offId = useId();
  const [ready, setReady] = useState(slot.ready);
  const [off, setOff] = useState(slot.off);
  const [offOn, setOffOn] = useState(slot.off !== '');
  const apply = (e: PlanEdit) => {
    const cmd = planEdit(ctx, e);
    if (cmd) void send(cmd);
    onClose();
  };
  const set = (all: boolean) =>
    apply({ op: 'set', day, window: slot.window, ready, off: offOn ? off : '', all });
  return (
    <Sheet
      variant="sheet"
      title={slot.title}
      titleId={titleId}
      onClose={onClose}
      closeLabel={ctx.tr('cancel')}
      closeTestId="planslot-close"
      testId="planslot-sheet"
    >
      <div className="stack plan-slot">
        <span className="field-label">{ctx.tr('planReady')}</span>
        <TimeWheel
          value={ready}
          onChange={setReady}
          label={ctx.tr('planReady')}
          hourLabel={ctx.tr('wheelHour')}
          minuteLabel={ctx.tr('wheelMinute')}
          testId="plan-ready"
        />
        <Toggle
          id={offId}
          label={ctx.tr('planOffAt')}
          hint={offOn ? undefined : ctx.tr('planOffAtHint')}
          checked={offOn}
          onChange={(e) => {
            // a time without an off time gets one two hours after the ready time set now
            if (e.target.checked && !slot.off) setOff(planOffFor(ready));
            setOffOn(e.target.checked);
          }}
          data-testid="plan-off-switch"
        />
        {offOn && (
          <TimeWheel
            value={off}
            onChange={setOff}
            rows={3}
            label={ctx.tr('planOffAt')}
            hourLabel={ctx.tr('wheelHour')}
            minuteLabel={ctx.tr('wheelMinute')}
            testId="plan-off"
          />
        )}
        <Button block onClick={() => set(false)} data-testid="plan-save">
          {ctx.tr('save')}
        </Button>
        <Button variant="secondary" block onClick={() => set(true)} data-testid="plan-all">
          {ctx.tr('planAllDays')}
        </Button>
        {slot.text && (
          <Button
            variant="danger"
            block
            onClick={() => apply({ op: 'remove', day, window: slot.window })}
            data-testid="plan-delete"
          >
            {ctx.tr('planDelete')}
          </Button>
        )}
      </div>
    </Sheet>
  );
}

/**
 * Holiday: the schedule pauses through a local day of the machine. While none is set, the date (tomorrow to start
 * with: an empty date field shows nothing on iOS, 23.10.2026) and "Pausieren", which sends it and closes the sheet;
 * nothing goes out before. While one lasts, until when and "Beenden".
 */
function HolidaySheet({ onDone }: { onDone: () => void }) {
  const { ctx, send } = useMachine();
  const h = powerView(ctx).holiday;
  const id = useId();
  const [day, setDay] = useState(h?.start ?? '');
  if (!h) return null;
  const cmd = holidayCommand(ctx, day);
  return (
    <div className="stack" data-testid="holiday-row">
      {h.until ? (
        <>
          <Row label={h.untilTitle ?? h.until} hint={h.hint}>
            <Button
              variant="secondary"
              size="sm"
              disabled={!h.editable}
              onClick={() => h.end && void send(h.end)}
              data-testid="holiday-end"
            >
              {ctx.tr('holidayEnd')}
            </Button>
          </Row>
          <Button block onClick={onDone} data-testid="holiday-done">
            {ctx.tr('done')}
          </Button>
        </>
      ) : (
        <>
          <DateField
            id={id}
            label={h.label}
            hideLabel
            hint={h.hint}
            min={h.min}
            value={day}
            placeholder={ctx.tr('dateChoose')}
            disabled={!h.editable}
            onChange={(e) => setDay(e.target.value)}
            data-testid="holiday-date"
          />
          <Button
            block
            disabled={!h.editable || !cmd}
            onClick={() => {
              if (cmd) void send(cmd);
              onDone();
            }}
            data-testid="holiday-save"
          >
            {ctx.tr('holidayPause')}
          </Button>
        </>
      )}
    </div>
  );
}

/** "BOOKOO" from "BOOKOO_SC_U 328844": the brand as the scale's title (board "Waage") */
const scaleBrand = (name: string): string => name.split(/[_\s]/)[0] || name;
/** "SC_U 328844" from "BOOKOO_SC_U 328844": what follows the brand, to tell two scales of one brand apart */
const scaleModel = (name: string): string => name.slice(scaleBrand(name).length).replace(/^[_\s]+/, '');

/**
 * The scale (board "Waage"): the chosen one as a tile (brand, name, connected and battery as pills, "Waage vergessen"),
 * or while none is chosen "Waage wählen" with what was found and "Suchen"; then its switches in a card of rows.
 */
function ScaleCards() {
  const { ctx } = useMachine();
  const [searched, setSearched] = useState(false);
  const s = scaleView(ctx, searched);
  const sg = ctx.view.groups.scale?.data as
    { status?: string; selected?: { name: string } | null; battery?: number | null } | undefined;
  const selected = sg?.selected ?? null;
  const connected = s.status.connected;
  return (
    <>
      <OwnerNote controls={[s.enabled, s.autoTare, s.learn]} />
      {selected && (
        <Card className="tab-card machine-tile scale-tile" data-testid="scale-known">
          <div className="device-head">
            <span className="device-icon" aria-hidden="true">
              <Icon name="scale" />
            </span>
            <span className="row-text">
              <span className="card-title">{scaleBrand(selected.name)}</span>
              <span className="hint">{selected.name}</span>
            </span>
          </div>
          <div className="tags">
            <Chip data-testid="scale-status">
              <StatusDot tone={connected ? 'ok' : 'neutral'} />
              {ctx.tr(connected ? 'scaleConnectedTag' : 'scaleDisconnectedTag')}
            </Chip>
            {connected && typeof sg?.battery === 'number' && (
              <Chip data-testid="scale-battery">{ctx.tr('scaleBatteryTag', { n: sg.battery })}</Chip>
            )}
          </div>
          {s.forget && (
            <ActionButton
              action={{ ...s.forget, label: ctx.tr('scaleForgetLong') }}
              size="md"
              block
              testId="scale-forget"
            />
          )}
        </Card>
      )}
      {s.search && (
        <Card className="tab-card" data-testid="scale-search">
          <CardHeader
            title={ctx.tr('scaleChoose')}
            info={{ text: ctx.tr('scaleChooseInfo'), label: ctx.tr('infoShow'), testId: 'scale-choose-info' }}
          />
          {s.found.length > 0 && (
            <ul className="list">
              {s.found.map((f) => (
                <li key={f.address} className="list-row" data-testid={`found-${f.address}`}>
                  {/* two lines that never wrap, like the tile of the chosen scale: the brand, below it the rest of the
                      name and the signal, which drops out first beside "Verbinden" (WRITING.md 0a); the whole name on
                      the first line wrapped at 360 px (UX-LINE.md "Gefundene Waagen", board "Waage" since 23.10.2026) */}
                  <div className="row-text">
                    <span className="row-label">{scaleBrand(f.name)}</span>
                    <FitLine className="hint" parts={[scaleModel(f.name), f.signal]} />
                  </div>
                  {/* one primary at most (UX line): connecting is one of several, a quiet button */}
                  <ActionButton action={f.connect} variant="secondary" testId="scale-connect" />
                </li>
              ))}
            </ul>
          )}
          {s.noneFound && <p className="hint">{s.noneFound}</p>}
          <ActionButton
            action={s.search.find}
            size="md"
            block
            testId="scale-find"
            onSent={() => setSearched(true)}
          />
        </Card>
      )}
      {!selected && !s.search && (
        <p className="hint owner-note" data-testid="scale-status">
          {s.status.text}
        </p>
      )}
      <Card className="tab-card rows-card" data-testid="scale-card">
        <ControlRow control={s.enabled} testId="scale-enabled" />
        <ControlRow control={s.autoTare} testId="auto-tare" />
        <ControlRow control={s.learn} testId="learn-lag" />
      </Card>
    </>
  );
}

/** The water tank (board "Wassertank"): the sensor and the tank's state now, the switch type in a card of its own. */
function TankCards() {
  const { ctx } = useMachine();
  const c = settingsCards(ctx).tank;
  return (
    <>
      <OwnerNote controls={[c.enabled, c.mode]} />
      <Card className="tab-card rows-card" data-testid="tank-card">
        <ControlRow control={c.enabled} testId="tank-enabled" />
        {c.now && (
          <Row label={c.now.label} testId="tank-now">
            <Chip>
              <StatusDot tone={c.now.empty ? 'warn' : 'ok'} />
              {c.now.text}
            </Chip>
          </Row>
        )}
      </Card>
      {c.mode && (
        <Card className="tab-card" data-testid="tank-mode-card">
          <CardHeader
            title={c.mode.label}
            info={
              c.mode.info ? { text: c.mode.info, label: ctx.tr('infoShow'), testId: 'tank-mode-info' } : null
            }
          />
          <ControlRow control={c.mode} bare testId="tank-mode" />
        </Card>
      )}
    </>
  );
}

/**
 * The display (board "Display"): the round display as it looks after a shot (the last one), the language in a card,
 * how long it shows "FERTIG" in another.
 */
function DisplayCards() {
  const { ctx, client, machineId } = useMachine();
  const c = settingsCards(ctx).display;
  const sv = useStore(client.shots(machineId));
  const last = sv.items[0] ? shotLine(ctx, sv.items[0]) : null;
  return (
    <>
      <OwnerNote controls={[c.language, c.postBrew]} />
      <div className="display-preview" aria-hidden="true" data-testid="display-preview">
        <span className="display-preview-word">{c.doneWord}</span>
        {last && <span className="display-preview-time num">{last.timeShort}</span>}
        {last?.weightText && <span className="display-preview-weight num">{last.weightText}</span>}
      </div>
      {c.language && (
        <Card className="tab-card" data-testid="display-card">
          <CardHeader title={c.language.label} />
          <ControlRow control={c.language} bare testId="display-language" />
        </Card>
      )}
      {c.postBrew && (
        <Card className="tab-card rows-card" data-testid="post-brew-card">
          <ControlRow control={c.postBrew} testId="post-brew" />
        </Card>
      )}
    </>
  );
}

/** The seldom-used pages, each with what it holds now (UX line: navigation = a row with a chevron). */
function MoreCard({ onPage }: { onPage: (p: SettingsPage) => void }) {
  const { ctx } = useMachine();
  const { view } = useAssistant();
  const s = scaleView(ctx, false);
  const c = settingsCards(ctx);
  // connected: the scale's brand and battery (board "BOOKOO · 82 %"; the page has the whole name)
  const sg = ctx.view.groups.scale?.data as
    { status?: string; selected?: { name: string } | null; battery?: number | null } | undefined;
  const scaleHint =
    s.status.connected && sg?.selected
      ? dotJoin([scaleBrand(sg.selected.name), typeof sg.battery === 'number' ? `${sg.battery} %` : ''])
      : null;
  return (
    <Card className="tab-card nav-card" data-testid="settings-more">
      <NavList
        items={[
          {
            key: 'scale',
            icon: 'scale',
            label: ctx.tr('sScale'),
            hint: scaleHint ?? s.status.text,
            onClick: () => onPage({ page: 'scale' }),
            testId: 'scale-open',
          },
          {
            key: 'tank',
            icon: 'tank',
            label: ctx.tr('sTank'),
            hint: dotJoin([
              c.tank.enabled ? `${c.tank.enabled.label} ${c.tank.enabled.text}` : '',
              c.tank.mode?.text ?? '',
            ]),
            onClick: () => onPage({ page: 'tank' }),
            testId: 'tank-open',
          },
          {
            key: 'display',
            icon: 'display',
            label: ctx.tr('sDisplay'),
            hint: dotJoin([
              c.display.language?.text ?? '',
              c.display.postBrew
                ? ctx.tr('postBrewRow', { w: c.display.doneWord, s: c.display.postBrew.text })
                : '',
            ]),
            onClick: () => onPage({ page: 'display' }),
            testId: 'display-open',
          },
          {
            key: 'claude',
            icon: 'sparkle',
            label: ctx.tr('sClaude'),
            hint: ctx.tr(view.account?.key ? 'claudeKeyYes' : 'claudeKeyNo'),
            onClick: () => onPage({ page: 'claude' }),
            testId: 'claude-open',
          },
          {
            key: 'advanced',
            icon: 'dial',
            label: ctx.tr('sPid'),
            hint: ctx.tr('advancedHint'),
            onClick: () => onPage({ page: 'advanced' }),
            testId: 'advanced-open',
          },
          {
            key: 'system',
            icon: 'info',
            label: ctx.tr('sSystem'),
            hint: ctx.tr('systemHint'),
            onClick: () => onPage({ page: 'system' }),
            testId: 'system-open',
          },
        ]}
      />
    </Card>
  );
}

/** Advanced (board "Fortgeschritten"): the heating in one card, the PID values with their (i) in another. */
function AdvancedCards() {
  const { ctx } = useMachine();
  const c = settingsCards(ctx).advanced;
  const rows = (xs: ControlView[]) => xs.map((x) => <ControlRow key={x.id} control={x} />);
  return (
    <>
      <OwnerNote controls={c.controls} />
      <Card className="tab-card rows-card" data-testid="advanced-card">
        <CardHeader title={ctx.tr('sHeating')} />
        {rows(c.heating)}
      </Card>
      {c.pid.length > 0 && (
        <Card className="tab-card rows-card" data-testid="pid-card">
          <CardHeader
            title={ctx.tr('sPidValues')}
            info={{ text: ctx.tr('pidInfo'), label: ctx.tr('infoShow'), testId: 'pid-info' }}
          />
          {rows(c.pid)}
        </Card>
      )}
    </>
  );
}

/**
 * System (board "System"): the device name as a field, the log level as a row with its value, the diagnosis with its
 * two buttons; three cards.
 */
/**
 * Board "System": everything about the device on one page, its figures and the restart first, the name, the log and the
 * diagnosis, the export, the resets last.
 */
function SystemCards({ onEnded }: { onEnded: () => void }) {
  const { ctx } = useMachine();
  const [host, level] = settingsCards(ctx).system.controls;
  return (
    <>
      <OwnerNote controls={[host, level]} />
      <DeviceCard />
      {host && (
        <Card className="tab-card" data-testid="system-card">
          <ControlRow control={host} />
        </Card>
      )}
      {level && (
        <Card className="tab-card rows-card" data-testid="log-level-card">
          <ControlRow control={level} />
        </Card>
      )}
      {/* the log and the last crash (W19) */}
      <DiagnosisBox />
      <ExportCard />
      <ResetCard onEnded={onEnded} />
    </>
  );
}

/** The machine in the account: name, role, unpairing by the owner (FR-12; moved here from the home screen). */
function MachineCard({ api, onUnpaired }: { api: PairingApi; onUnpaired: () => void }) {
  const { ctx, machineId } = useMachine();
  const [unpairing, setUnpairing] = useState(false);
  const [error, setError] = useState('');
  const titleId = useId();
  const info = ctx.view.info;
  const unpair = async () => {
    setError('');
    const r = await api.unpair(machineId);
    setUnpairing(false);
    if (!r.ok) {
      setError(pairingErrorText(ctx.lang, r.error));
      return;
    }
    onUnpaired();
  };
  const conn = connLine(ctx);
  const fw = deviceView(ctx).facts.find((f) => f.key === 'fw')?.value;
  const owner = info?.role === 'owner';
  // the machine's tile (prototype "Reiter Einstellungen"): icon, name, firmware, the state and the role as pills, unpairing
  // as the last row in red
  return (
    <Card className="tab-card machine-tile" data-testid="machine-card" aria-label={ctx.tr('sMachine')}>
      <div className="device-head">
        <span className="device-icon" aria-hidden="true">
          <Icon name="machine" />
        </span>
        <span className="row-text">
          <span className="card-title" data-testid="machine-name">
            {info?.name ?? machineId}
          </span>
          {fw && fw !== '–' && <span className="hint">{`${ctx.tr('fwVersion')} ${fw}`}</span>}
        </span>
      </div>
      <div className="tags">
        <Chip data-testid="machine-conn">
          <StatusDot tone={conn.tone} />
          {conn.presence === 'offline' ? ctx.tr('conn_offline') : conn.text}
        </Chip>
        <Chip data-testid="machine-role">{ctx.tr(owner ? 'tagOwner' : 'tagMember')}</Chip>
      </div>
      {owner && (
        <>
          <button
            type="button"
            className="row-danger"
            onClick={() => setUnpairing(true)}
            data-testid="unpair"
          >
            <Icon name="unlink" />
            {ctx.tr('unpairMachine')}
          </button>
          <span className="field-error" role="status" data-testid="unpair-result">
            {error}
          </span>
        </>
      )}
      {unpairing && (
        <Sheet
          variant="alert"
          title={t(ctx.lang, 'unpairTitle')}
          titleId={titleId}
          onClose={() => setUnpairing(false)}
        >
          <p className="text-dim">{t(ctx.lang, 'unpairText')}</p>
          <div className="sheet-actions">
            <Button variant="danger" block onClick={() => void unpair()} data-testid="unpair-confirm">
              {t(ctx.lang, 'unpair')}
            </Button>
            <Button variant="secondary" block onClick={() => setUnpairing(false)} data-testid="unpair-cancel">
              {t(ctx.lang, 'cancel')}
            </Button>
          </div>
        </Sheet>
      )}
    </Card>
  );
}
